"use client";

// Assign & govern one policy: pick the owning group, then a steward from that
// group's chairs/vice-chairs (the only eligible stewards), nominate/ratify,
// tag the approval route, record gate approvals, and manage external advisors.

import { useEffect, useState } from "react";
import Link from "next/link";
import {
    assignSteward, ratifySteward, searchUsers,
    getConsoleGroups, getGroupLeaders,
    getGovernance, setApprovalRoute, setBoardInvolvement, approveGate, revokeGate,
    addAdvisor, removeAdvisor,
} from "@/lib/policies/api";

const ROUTES: { value: string | null; label: string; hint: string }[] = [
    { value: null,         label: "— not classified —", hint: "No gate requirement yet" },
    { value: "group",      label: "Group-level",        hint: "Group self-approves" },
    { value: "governance", label: "Governance",         hint: "Governance Committee approval" },
    { value: "board",      label: "Board",              hint: "Board vote" },
];

const INVOLVEMENTS: { value: string | null; label: string; hint: string }[] = [
    { value: null,       label: "— not classified —", hint: "Full board chain required" },
    { value: "none",     label: "No board involvement", hint: "Applied → Published directly" },
    { value: "informed", label: "Informed to Board",  hint: "Board informed, no vote" },
    { value: "approval", label: "Board approval",     hint: "Full board vote required" },
];

export function AssignmentModal({ policy, onClose, onDone }: { policy: any; onClose: () => void; onDone: () => void }) {
    // ── Steward assignment ──
    const [groups, setGroups] = useState<any[]>([]);
    const [groupKey, setGroupKey] = useState<string>(
        policy.assignment?.owning_entity_type && policy.assignment?.owning_entity_id != null
            ? `${policy.assignment.owning_entity_type}:${policy.assignment.owning_entity_id}` : ""
    );
    const [leaders, setLeaders] = useState<any[] | null>(null);
    const [selected, setSelected] = useState<any>(null);
    const [saving, setSaving] = useState<"nominate" | "ratify" | null>(null);
    const [error, setError] = useState<string | null>(null);
    // Confirm-or-override: the recommended owning group (existing assignment,
    // else the seeded proposed_owner_group matched against committees).
    const [recommendedKey, setRecommendedKey] = useState<string>("");
    const [overrideReason, setOverrideReason] = useState("");

    // ── Governance (route / gates / advisors) ──
    const [gov, setGov] = useState<any>(null);
    const [advisorQuery, setAdvisorQuery] = useState("");
    const [advisorResults, setAdvisorResults] = useState<any[]>([]);

    useEffect(() => {
        getConsoleGroups().then((gs) => {
            setGroups(gs);
            if (groupKey) {
                setRecommendedKey(groupKey);
            } else if (policy.proposed_owner_group) {
                const want = policy.proposed_owner_group.toLowerCase();
                const m = gs.find((g: any) =>
                    want.includes(g.name.toLowerCase()) || g.name.toLowerCase().includes(want));
                if (m) {
                    const key = `${m.entity_type}:${m.entity_id}`;
                    setRecommendedKey(key);
                    setGroupKey(key);
                }
            }
        }).catch((e) => setError(e.message));
        getGovernance(policy.id).then(setGov).catch((e) => setError(e.message));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [policy.id]);

    useEffect(() => {
        setSelected(null);
        setLeaders(null);
        if (!groupKey) return;
        const [etype, eid] = groupKey.split(":");
        getGroupLeaders(etype, eid).then(setLeaders).catch((e) => setError(e.message));
    }, [groupKey]);

    const isOverride = !!recommendedKey && !!groupKey && groupKey !== recommendedKey;

    const assignPayload = () => {
        const [etype, eid] = groupKey.split(":");
        return {
            steward_user_id: selected.id,
            owning_entity_type: etype,
            owning_entity_id: Number(eid),
            role: selected.role,
            override_reason: isOverride ? overrideReason : undefined,
        };
    };

    const nominate = async () => {
        if (!selected || !groupKey) return;
        setSaving("nominate");
        setError(null);
        try {
            await assignSteward(policy.id, assignPayload());
            onDone();
        } catch (e: any) {
            setError(e.message);
        } finally {
            setSaving(null);
        }
    };

    const ratify = async () => {
        setSaving("ratify");
        setError(null);
        try {
            if (selected && groupKey) await assignSteward(policy.id, assignPayload());
            await ratifySteward(policy.id);
            onDone();
        } catch (e: any) {
            setError(e.message);
        } finally {
            setSaving(null);
        }
    };

    const changeRoute = async (v: string) => {
        let reason: string | undefined;
        if (gov?.approval_route && v !== gov.approval_route) {
            reason = window.prompt("Changing an already-set approval route — why?")?.trim() || undefined;
            if (!reason) return;
        }
        try {
            setGov(await setApprovalRoute(policy.id, v || null, reason));
        } catch (e: any) { setError(e.message); }
    };

    const changeInvolvement = async (v: string) => {
        let reason: string | undefined;
        if (gov?.board_involvement && v !== gov.board_involvement) {
            reason = window.prompt("Changing an already-set board involvement — why?")?.trim() || undefined;
            if (!reason) return;
        }
        try {
            setGov(await setBoardInvolvement(policy.id, v || null, reason));
        } catch (e: any) { setError(e.message); }
    };

    const toggleGate = async (gate: any) => {
        try {
            setGov(gate.approved ? await revokeGate(policy.id, gate.gate) : await approveGate(policy.id, gate.gate));
        } catch (e: any) { setError(e.message); }
    };

    const searchAdvisors = async (q: string) => {
        setAdvisorQuery(q);
        if (q.length < 2) { setAdvisorResults([]); return; }
        setAdvisorResults(await searchUsers(q));
    };

    const grantAdvisor = async (u: any) => {
        setAdvisorQuery("");
        setAdvisorResults([]);
        try {
            setGov(await addAdvisor(policy.id, u.id, "External advisor (exception path)"));
        } catch (e: any) { setError(e.message); }
    };

    const revokeAdvisor = async (advisorId: string) => {
        try {
            setGov(await removeAdvisor(policy.id, advisorId));
        } catch (e: any) { setError(e.message); }
    };

    const hint = policy.proposed_steward;

    return (
        <div onClick={onClose} className="fixed inset-0 z-[100] bg-[rgba(12,34,38,0.45)] flex items-center justify-center p-6">
            <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-[560px] max-w-full shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
                <div className="border-b border-[#eef1ef] flex items-start justify-between gap-3" style={{ padding: "22px 26px" }}>
                    <div>
                        <div className="text-[11px] tracking-wide font-bold text-[#88a39b]">ASSIGN &amp; GOVERN POLICY</div>
                        <div className="font-bold text-lg mt-1 text-[#15302f]">{policy.id} · {policy.name}</div>
                        <div className="text-xs text-[#5f7173] mt-1">{policy.proposed_owner_group}</div>
                    </div>
                    <Link
                        href={`/dashboard/policies/workspace/${policy.id}`}
                        className="flex-none border border-[#4b8892] text-[#3a6b78] text-[11.5px] font-bold rounded-lg px-3 py-1.5 no-underline"
                        title="Staff can view and edit any policy workspace"
                    >
                        Open workspace
                    </Link>
                </div>

                <div className="overflow-y-auto" style={{ padding: "22px 26px" }}>
                    {error && (
                        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 mb-4">{error}</div>
                    )}
                    {hint && (
                        <div className="text-[11px] text-[#9a6a16] bg-[#fbecd6] rounded-lg px-3 py-2.5 mb-4">
                            Pre-filled by name from the board roster: <strong>{hint}</strong>. Stewards must be a chair or
                            vice-chair of the owning group — pick the group, then the leader.
                        </div>
                    )}
                    {policy.assignment?.steward_name && (
                        <div className="bg-[#f6f8f7] border border-[#e6ebe9] rounded-lg p-3 text-sm mb-4">
                            Current: <span className="font-bold">{policy.assignment.steward_name}</span>
                            {" — "}
                            <span className="text-xs uppercase font-bold text-[#88a39b]">{policy.assignment.state}</span>
                        </div>
                    )}

                    <label className="text-[11px] tracking-wide font-bold text-[#88a39b]">OWNING COMMITTEE</label>
                    {recommendedKey && groupKey === recommendedKey && (
                        <p className="text-[11px] text-[#2c7a63] mt-1">
                            This is the recommended owning group — reason to believe otherwise? Pick a different committee below.
                        </p>
                    )}
                    <select
                        value={groupKey}
                        onChange={(e) => { setGroupKey(e.target.value); setOverrideReason(""); }}
                        className="w-full mt-1.5 mb-4 border border-[#cfd9d6] rounded-lg px-3 py-2.5 text-[13.5px] text-[#15302f] outline-none focus:border-[#4b8892] bg-white"
                    >
                        <option value="">Select the owning committee…</option>
                        {groups.map((g) => (
                            <option key={`${g.entity_type}:${g.entity_id}`} value={`${g.entity_type}:${g.entity_id}`}>
                                {g.name}
                            </option>
                        ))}
                    </select>
                    {isOverride && (
                        <div className="mb-4 -mt-2">
                            <label className="text-[11px] tracking-wide font-bold text-[#9a6a16]">
                                OVERRIDE REASON <span className="font-normal">· you&apos;re changing the recommended owning group — say why (audited)</span>
                            </label>
                            <textarea
                                value={overrideReason}
                                onChange={(e) => setOverrideReason(e.target.value)}
                                rows={2}
                                placeholder="e.g. subject matter actually belongs to the Academia Outreach Committee…"
                                className="w-full mt-1.5 border border-[#e5c98f] rounded-lg px-3 py-2 text-[13px] text-[#15302f] outline-none focus:border-[#9a6a16] bg-[#fffdf7]"
                            />
                        </div>
                    )}

                    <label className="text-[11px] tracking-wide font-bold text-[#88a39b]">
                        PRIMARY STEWARD <span className="text-[#9a6a16]">· chair / vice-chair only · (Governance Chair to ratify)</span>
                    </label>
                    {!groupKey ? (
                        <p className="text-xs text-[#88a39b] mt-1.5 mb-3 italic">Pick the owning group first.</p>
                    ) : leaders === null ? (
                        <p className="text-xs text-[#88a39b] mt-1.5 mb-3 italic">Loading group leaders…</p>
                    ) : leaders.length === 0 ? (
                        <p className="text-xs text-[#b5651d] mt-1.5 mb-3">
                            This group has no active chair or vice-chair — add one in the group roster before assigning.
                        </p>
                    ) : (
                        <div className="border border-gray-200 rounded-lg divide-y max-h-44 overflow-y-auto mt-1.5 mb-3">
                            {leaders.map((u) => (
                                <button
                                    key={u.id}
                                    onClick={() => setSelected(u)}
                                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${selected?.id === u.id ? "bg-[#edf7f8]" : ""}`}
                                >
                                    <span className="font-bold text-[#15302f]">{u.name}</span>
                                    <span className="text-gray-400 text-xs"> · {u.role} · {u.email}</span>
                                </button>
                            ))}
                        </div>
                    )}

                    {/* ── Approval routing (Phase D) ── */}
                    {gov && (
                        <div className="border-t border-[#eef1ef] pt-4 mt-4">
                            <label className="text-[11px] tracking-wide font-bold text-[#88a39b]">APPROVAL ROUTE</label>
                            <select
                                value={gov.approval_route || ""}
                                onChange={(e) => changeRoute(e.target.value)}
                                className="w-full mt-1.5 border border-[#cfd9d6] rounded-lg px-3 py-2.5 text-[13.5px] text-[#15302f] outline-none focus:border-[#4b8892] bg-white"
                            >
                                {ROUTES.map((r) => (
                                    <option key={r.label} value={r.value || ""}>{r.label} — {r.hint}</option>
                                ))}
                            </select>
                            <label className="text-[11px] tracking-wide font-bold text-[#88a39b] mt-3 block">BOARD INVOLVEMENT</label>
                            <select
                                value={gov.board_involvement || ""}
                                onChange={(e) => changeInvolvement(e.target.value)}
                                className="w-full mt-1.5 border border-[#cfd9d6] rounded-lg px-3 py-2.5 text-[13.5px] text-[#15302f] outline-none focus:border-[#4b8892] bg-white"
                            >
                                {INVOLVEMENTS.map((r) => (
                                    <option key={r.label} value={r.value || ""}>{r.label} — {r.hint}</option>
                                ))}
                            </select>
                            {gov.gates.length > 0 && (
                                <div className="mt-3 space-y-2">
                                    {gov.gates.map((g: any) => (
                                        <div key={g.gate} className="flex items-center justify-between gap-3 bg-[#f6f8f7] border border-[#e6ebe9] rounded-lg px-3 py-2">
                                            <div>
                                                <p className="text-[12.5px] font-bold text-[#15302f]">{g.label}</p>
                                                {g.approved && (
                                                    <p className="text-[10.5px] text-[#88a39b]">
                                                        Approved{g.approved_by ? ` by ${g.approved_by}` : ""}{g.approved_at ? ` · ${new Date(g.approved_at).toLocaleDateString()}` : ""}
                                                    </p>
                                                )}
                                            </div>
                                            <button
                                                onClick={() => toggleGate(g)}
                                                className={`text-[11px] font-bold rounded-lg px-3 py-1.5 border ${
                                                    g.approved
                                                        ? "border-[#d9dedb] bg-white text-[#5f7173]"
                                                        : "border-none bg-[#004d57] text-white"
                                                }`}
                                            >
                                                {g.approved ? "Revoke" : "Record approval"}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* ── External advisors (exception path) ── */}
                            <div className="mt-4">
                                <label className="text-[11px] tracking-wide font-bold text-[#88a39b]">
                                    EXTERNAL ADVISORS <span className="text-[#9a6a16]">· exception path (legal / audit)</span>
                                </label>
                                {gov.advisors.map((a: any) => (
                                    <div key={a.id} className="flex items-center justify-between gap-3 bg-[#f6f8f7] border border-[#e6ebe9] rounded-lg px-3 py-2 mt-1.5">
                                        <p className="text-[12.5px] text-[#15302f]"><span className="font-bold">{a.name}</span></p>
                                        <button onClick={() => revokeAdvisor(a.id)} className="text-[11px] font-bold text-[#ae232d]">Revoke</button>
                                    </div>
                                ))}
                                <input
                                    value={advisorQuery}
                                    onChange={(e) => searchAdvisors(e.target.value)}
                                    placeholder="Grant temporary edit access — search by name or email…"
                                    className="w-full mt-1.5 border border-[#cfd9d6] rounded-lg px-3 py-2.5 text-[13px] text-[#15302f] outline-none focus:border-[#4b8892]"
                                />
                                {advisorResults.length > 0 && (
                                    <div className="border border-gray-200 rounded-lg divide-y max-h-36 overflow-y-auto mt-1">
                                        {advisorResults.map((u) => (
                                            <button key={u.id} onClick={() => grantAdvisor(u)} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50">
                                                {u.name} <span className="text-gray-400 text-xs">{u.email}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <div className="bg-[#f4f6f5] border-t border-[#e6ebe9] flex justify-end gap-2.5" style={{ padding: "16px 26px" }}>
                    <button onClick={onClose} className="border border-[#d9dedb] bg-white text-[#5f7173] text-[13px] font-bold rounded-lg px-4 py-2">
                        Close
                    </button>
                    <button
                        onClick={nominate}
                        disabled={!selected || !groupKey || !!saving || (isOverride && !overrideReason.trim())}
                        className="border border-[#4b8892] bg-white text-[#3a6b78] text-[13px] font-bold rounded-lg px-4 py-2 disabled:opacity-50"
                    >
                        {saving === "nominate" ? "Saving…" : "Nominate (propose)"}
                    </button>
                    <button
                        onClick={ratify}
                        disabled={((!selected || !groupKey) && !policy.assignment?.steward_user_id) || !!saving || (isOverride && !overrideReason.trim())}
                        className="border-none bg-[#004d57] text-white text-[13px] font-bold rounded-lg px-4.5 py-2 disabled:opacity-50"
                    >
                        {saving === "ratify" ? "Ratifying…" : "Ratify steward"}
                    </button>
                </div>
            </div>
        </div>
    );
}
